import { Inject, Injectable, Optional } from '@angular/core';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { IPlaylistApi } from '../api/interfaces/playlist-abstract.api';
import { PlaylistApi } from '../api/playlist.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import { Playlist, Playlists } from '../models/playlist.model';
import {
  AbstractPaginableFacade,
  ItemsPaginableState,
} from './abstract-paginable.facade';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from 'app/services/toaster.service';

// 3 Minutes
const CACHE_VALIDITY = 180000;

@Injectable({
  providedIn: 'root',
})
export class PlaylistsFacade extends AbstractPaginableFacade<
  Playlist,
  ItemsPaginableState<Playlist>
> {
  public readonly playlists$: Observable<Playlists>;
  public readonly total$: Observable<number>;
  public readonly loading$: Observable<boolean>;

  constructor(
    private readonly playlistApi: IPlaylistApi,
    @Inject(NEVER_TOKEN) @Optional() private readonly userId: number,
    private toasterService: ToasterService,
    private translate: TranslateService
  ) {
    super(
      {
        loading: false,
        total: null,
        items: [],
        lastRequestedIndex: 0,
        lastLoadingTime: 0,
      },
      CACHE_VALIDITY
    );
    this.playlists$ = this.state$.pipe(
      map((state) => state.items),
      distinctUntilChanged()
    );
    this.total$ = this.state$.pipe(
      map((state) => state.total),
      distinctUntilChanged()
    );
    this.loading$ = this.state$.pipe(
      map((state) => state.loading),
      distinctUntilChanged()
    );
  }

  loadFromApi(start: number, end: number) {
    // Do not reload if a page is currently loading
    if (end <= 0 || this.isTotallyLoaded() || this.state.loading) {
      return;
    }

    const { page, pageSize } = this.computePagesAndSizeFromInterval(start, end);
    const offset = (page - 1) * pageSize;

    this.setState({
      loading: true,
      lastRequestedIndex: end,
    });

    this.playlistApi.getPlaylists(page, pageSize, this.userId).subscribe({
      next: ({ data: newPlaylists, meta }) => {
        const allPlaylists = this.mergeNewItemsToOldOnes(newPlaylists, offset);
        this.cacheValidityTime = Date.now() + this.cacheValidity;
        this.setState({
          loading: false,
          items: allPlaylists,
          total: meta.total,
        });
        // TODO => Run following data in queue
      },
      error: (err) => {
        console.error(err);
        this.setState({
          loading: false,
        });
      },
    });
  }

  deletePlaylist(playlistId: number) {
    (this.playlistApi as PlaylistApi).deletePlaylist(playlistId).subscribe({
      error: () => {
        const error: string = this.translate.instant(
          'dashboard.manage-content.popup.confirm.delete-content-error'
        );
        this.toasterService.warning(null, error);
      },
      next: () => {
        const success: string = this.translate.instant(
          'dashboard.manage-content.popup.confirm.delete-content-success'
        );
        this.toasterService.success(null, success);
        const playlistIndex: number = this.state.items.findIndex(
          (playlist: Playlist) => playlist.id === playlistId
        );
        if (playlistIndex === -1) {
          return;
        }
        const newPlaylists: Playlist[] = [...this.state.items];
        newPlaylists.splice(playlistIndex, 1);
        this.setState({
          items: newPlaylists,
          total: this.state.total - 1,
          lastRequestedIndex: this.state.lastRequestedIndex - 1,
        });
      },
    });
  }
}
